import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddBtn from '../../../../components/Buttons/AddBtn';
import RemoveBtn from '../../../../components/Buttons/RemoveBtn';
import Modal from '../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../components/Notification';
import { NotificationTypes } from '../../../../components/Notification/notificationEnums';
import SingleSelect from '../../../../components/SingleSelect';
import HttpStatus from '../../../../enums/httpStatus';
import { authSettingsAPI } from '../../../../services/requests';
import { AuthSetting } from '../../../../types/apiResponse/accessControl';
import { TextInputOnChangeEvent } from '../../../../types/events';
import {
  authTypesSelectOptions,
  getBooleanOptions,
  getOptionByValue,
  getSelectOptionByBoolean,
} from '../../../../utils/getSelectOptions';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  authSettingSelected: AuthSetting | null;
  callback: () => void;
};

export const AuthSettingUpInsertModal = ({
  showModal,
  closeModal,
  authSettingSelected,
  callback,
}: Props) => {
  const initialData: AuthSetting = authSettingSelected ?? {
    id: 0,
    tenant_id: 0,
    active: false,
    auth_type_name: 'smartsell',
    data: null,
  };

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [authSetting, setAuthSetting] = useState<AuthSetting>(initialData);
  const [authSettingData, setAuthSettingData] = useState<Record<string, any> | null>(
    initialData.data,
  );

  const [newKeyName, setNewKeyName] = useState<string>('');
  const [newKeyValue, setNewKeyValue] = useState<string>('');

  const sendRequest = async () => {
    setIsLoading(true);

    try {
      authSettingsAPI.query = '';

      const dataToRequest = {
        auth_type_name: authSetting.auth_type_name,
        active: authSetting.active,
        data: authSettingData,
      };

      const response = await authSettingsAPI.store(dataToRequest);

      if (response.status === HttpStatus.OK) {
        frontendNotification({
          message: t('success'),
          type: NotificationTypes.SUCCESS,
        });
      } else {
        throw response;
      }
      callback();
      closeModal();
    } catch (err) {
      if (!authSettingsAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onClickActionBtn = () => {
    if (!authSetting || !authSetting.auth_type_name) {
      frontendNotification({ message: t('invalidType'), type: 'warning' });
      return;
    }

    sendRequest();
  };

  const onClickAddNewFieldBtn = () => {
    setAuthSettingData({
      ...authSettingData,
      [newKeyName]: newKeyValue,
    });

    setNewKeyName('');
    setNewKeyValue('');
  };

  const onClickRemoveFieldBtn = (fieldKey: string) => {
    if (authSettingData) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [fieldKey]: _, ...rest } = authSettingData;
      setAuthSettingData(rest);
    }
  };

  const handleInputChange = (fieldKey: string, value: string) => {
    if (authSettingData) {
      setAuthSettingData({
        ...authSettingData,
        [fieldKey]: value,
      });
    }
  };

  const handleRenderDinamicDataFields = () => {
    if (!authSettingData) {
      return null;
    }

    return (
      <>
        {Object.keys(authSettingData).map((key) => (
          <div className="row" key={key}>
            <div className="inputWithLabel">
              <label className="label">{key}</label>
              <input
                type="text"
                value={(authSettingData as Record<string, string>)[key]}
                onChange={(e) => handleInputChange(key, e.target.value)}
              />
            </div>
            <div className="inputWithLabel" style={{ maxWidth: 'max-content' }}>
              <RemoveBtn onClick={() => onClickRemoveFieldBtn(key)} />
            </div>
          </div>
        ))}
      </>
    );
  };

  return (
    <Modal
      title={t('authSetting')}
      showModal={showModal}
      cancelButton={closeModal}
      action={onClickActionBtn}
      actionNameBtn={t('save')}
      closeModal={closeModal}
      isLoading={isLoading}
      bodyStyle={{ overflow: 'scroll' }}
    >
      <>
        <div className="row">
          <div className="inputWithLabel">
            <label className="label">{t('type') as string}</label>
            <SingleSelect
              options={authTypesSelectOptions()}
              style={{ minWidth: '100%' }}
              value={getOptionByValue(authSetting.auth_type_name, authTypesSelectOptions())}
              onChange={(v: any) => setAuthSetting({ ...authSetting, auth_type_name: v.value })}
            />
          </div>

          <div className="inputWithLabel" style={{ maxWidth: '120px' }}>
            <label className="label">{t('active') as string}</label>
            <SingleSelect
              options={getBooleanOptions()}
              style={{ minWidth: '100%' }}
              value={getSelectOptionByBoolean(authSetting.active)}
              onChange={(v: any) => setAuthSetting({ ...authSetting, active: v.value })}
            />
          </div>
        </div>

        <div className="division" />
        <p className="labelSection">{t('addField') as string}</p>

        <div className="row">
          <div className="inputWithLabel" style={{ maxWidth: '200px' }}>
            <label className="label">{t('name') as string}</label>
            <input
              type="text"
              className="input"
              value={newKeyName}
              onChange={(v: TextInputOnChangeEvent) => setNewKeyName(v.target.value)}
            />
          </div>

          <div className="inputWithLabel" style={{ maxWidth: '200px' }}>
            <label className="label">{t('value') as string}</label>
            <input
              type="text"
              className="input"
              value={newKeyValue}
              onChange={(v: TextInputOnChangeEvent) => setNewKeyValue(v.target.value)}
            />
          </div>

          <div className="inputWithLabel" style={{ maxWidth: 'max-content' }}>
            <AddBtn onClick={() => onClickAddNewFieldBtn()} />
          </div>
        </div>

        <div className="division" />
        <p className="labelSection">{t('fields') as string}</p>

        {handleRenderDinamicDataFields()}
      </>
    </Modal>
  );
};

export default AuthSettingUpInsertModal;
